import type { Partner } from "@expert/shared-types";
import type { Draft, WritableDraft } from "immer";

import { getAppConfigSingleton } from "@expert/app-config";
import { getLogger } from "@expert/logging";

import { env } from "@/config";
import { hasPendingCallback } from "@/sdk/sessions/hooks/useCallback";
import { onSessionEnd, onSessionStart } from "@/utils/setupSessionLifecycleManager";

import type { AgentSdkBase, Task, TaskCancelledReason, TaskCompletedReason } from "../agent";
import type { SessionStore } from "./session.store";
import type { CustomerSessionEndedReason, CustomerSessionStartedReason, Session } from "./types";

import { isChatTask } from "../agent/chat";
import { sdkEventBus } from "../agent/eventBus";
import { useAgentStore } from "../agent/store";
import { isTrainingTask } from "../agent/trainingTask";
import { isOutboundVoiceTask, isVoiceTask } from "../agent/voice";
import { useCallbackStore } from "./callback.store";
import { clearCallbackState } from "./getters";
import { SessionInstance } from "./session";
import { createIdleSession, updateTaskStatusInternal, useSessionStore } from "./session.store";

interface SessionStartArgs {
    currentSessionId: string;
    reason: CustomerSessionStartedReason;
    task: Task;
    agentSdk: AgentSdkBase;
}

interface SessionEndArgs {
    sessionAboutToEndId: string;
    reason: CustomerSessionEndedReason;
    triggeringTask: Task | undefined;
    currentPartner: Partner;
    agentSdk: AgentSdkBase;
}

/** Queues session operations for batch processing (supports concurrent task rehydration) */
interface SessionControlsState {
    sessionStartArgsQueue: SessionStartArgs[];
    sessionEndArgsQueue: SessionEndArgs[];
}

/** Queue-based API to defer session lifecycle triggers until after store mutations complete */
interface SessionControls {
    addSessionStartArgs: (args: SessionStartArgs) => void;
    addSessionEndArgs: (args: SessionEndArgs) => void;
}

const logger = getLogger({
    module: "sessionsOrchestrator",
});

// Check the idleSession timestamp to see if it needs to be recycled after 24 hours
sdkEventBus.on("session_changed", () => {
    const currentTimestamp = Date.now();
    const oldIdleSession = useSessionStore.getState().idleSession;

    if (currentTimestamp - oldIdleSession.startedTimestamp >= 24 * 60 * 60 * 1000) {
        const newIdleSession = createIdleSession();

        logger.info({ oldIdleSession, newIdleSession }, "Recycling idle session after 24 hours");

        useSessionStore.getState().mutateStore((state) => {
            state.idleSession = newIdleSession;
        });
    }
});

const tryCatch = (fn: () => void) => {
    try {
        fn();
    } catch (error) {
        logger.warn({ error, errorMessage: (error as Error)?.message }, "Error in session orchestrator");
    }
};

const setUpSessionControlsWrapper = () => {
    const state: SessionControlsState = {
        sessionStartArgsQueue: [],
        sessionEndArgsQueue: [],
    };

    const sessionControls: SessionControls = {
        addSessionStartArgs: (args: SessionStartArgs) => {
            state.sessionStartArgsQueue.push(args);
            // oxlint-disable-next-line no-unused-vars
            const { agentSdk, task, ...restArgs } = args;
            logger.debug(
                {
                    sessionStartArgs: { ...restArgs, task: task?.toLog() },
                    queueLength: state.sessionStartArgsQueue.length,
                },
                "Queuing session start",
            );
        },
        addSessionEndArgs: (args: SessionEndArgs) => {
            state.sessionEndArgsQueue.push(args);
            // oxlint-disable-next-line no-unused-vars
            const { agentSdk, triggeringTask, ...restArgs } = args;
            logger.debug(
                {
                    sessionEndArgs: { ...restArgs, triggeringTask: triggeringTask?.toLog() },
                    queueLength: state.sessionEndArgsQueue.length,
                },
                "Queuing session end",
            );
        },
    };

    return { sessionControlsState: state, sessionControls };
};

/** Processes all queued session operations after store mutation completes */
const cleanupSessionControlsWrapper = (sessionControlsState: SessionControlsState) => {
    // Process ends first to clean up stale sessions before creating new ones
    for (const endArgs of sessionControlsState.sessionEndArgsQueue) {
        // oxlint-disable-next-line no-unused-vars
        const { agentSdk, triggeringTask, ...rest } = endArgs;
        logger.debug(
            { sessionEndArgs: { ...rest, triggeringTask: triggeringTask?.toLog() } },
            "Triggering session end on cleanup",
        );
        triggerCxSessionEnd(endArgs);
    }

    // Then process session starts
    for (const startArgs of sessionControlsState.sessionStartArgsQueue) {
        // oxlint-disable-next-line no-unused-vars
        const { agentSdk, task, ...rest } = startArgs;
        logger.debug(
            { sessionStartArgs: { ...rest, task: (task as Task | undefined)?.toLog() } },
            "Triggering session start on cleanup",
        );
        triggerCxSessionStart(startArgs);
    }
};

const withSessionControlsCleanup = (fn: (sessionControls: SessionControls) => void) => {
    const { sessionControlsState, sessionControls } = setUpSessionControlsWrapper();

    tryCatch(() => {
        fn(sessionControls);
    });

    cleanupSessionControlsWrapper(sessionControlsState);
};

const initSessions = (agentSdk: AgentSdkBase, tasks: Task[]) => {
    const callbackSessionId = useCallbackStore.getState().callbackState?.sessionId;
    withSessionControlsCleanup((sessionControls) => {
        useSessionStore.getState().mutateStore((state) => {
            return initStore(sessionControls, state, callbackSessionId, tasks, agentSdk);
        });
    });
};

const addNewAgentTask = (task: Task, agentSdk: AgentSdkBase) => {
    withSessionControlsCleanup((sessionControls) => {
        if (isVoiceTask(task)) {
            const currentSession = useSessionStore.getState().computed.selectedSession();

            // Ad-hoc outbound call, new session
            if (task.callDirection === "outbound" && !task.previousTaskId) {
                sessionControls.addSessionStartArgs({
                    currentSessionId: currentSession?.id,
                    reason: "AdHocOutboundCall",
                    task,
                    agentSdk,
                });
            } else if (task.callDirection === "inbound") {
                // New incoming call, new session
                sessionControls.addSessionStartArgs({
                    currentSessionId: currentSession?.id,
                    reason: "InboundCall",
                    task,
                    agentSdk,
                });
            } else {
                const sessionId = task.sessionId ?? currentSession?.id;
                useSessionStore.getState().addTaskToSession(sessionId, task);
                useSessionStore.getState().setSelectedSession(sessionId);
            }
            return;
        }

        if (isTrainingTask(task)) {
            const currentSession = useSessionStore.getState().computed.selectedSession();

            // New training, new session
            sessionControls.addSessionStartArgs({
                currentSessionId: currentSession?.id,
                reason: "IncomingTraining",
                task,
                agentSdk,
            });
            return;
        }

        if (isChatTask(task)) {
            const currentSession = useSessionStore.getState().computed.selectedSession();

            sessionControls.addSessionStartArgs({
                currentSessionId: currentSession?.id,
                reason: "InboundChat",
                task,
                agentSdk,
            });
            return;
        }

        throw new Error("Unsupported task type");
    });
};

const onTaskRejected = (task: Task, agentSdk: AgentSdkBase) => {
    tryCatch(() => {
        const currentSession = useSessionStore.getState().computed.selectedSession();

        triggerCxSessionEnd({
            sessionAboutToEndId: currentSession?.id,
            reason: "CallRejected",
            triggeringTask: task,
            currentPartner: task.partner,
            agentSdk,
        });
    });
};

const onTaskCancelled = (task: Task, agentSdk: AgentSdkBase, reason: TaskCancelledReason) => {
    withSessionControlsCleanup((sessionControls) => {
        useSessionStore.getState().mutateStore((state) => {
            const storeTask = state.getTask(task.id);
            if (!storeTask) throw new Error(`Task ${task.id} not found in store`);

            const sessionIdForGivenTask = getSessionIdForTask(task, storeTask, state);
            if (!sessionIdForGivenTask) {
                throw new Error(`Task ${task.id} does not have a valid sessionId`);
            }

            updateTaskStatusInternal(state, task.id, "completed", reason);

            const endSessionReason: CustomerSessionEndedReason =
                reason === "TechnicalIssues"
                    ? "TechnicalIssues"
                    : isChatTask(storeTask)
                      ? "ChatEnded"
                      : "CallCompletedWithoutCallback";

            sessionControls.addSessionEndArgs({
                sessionAboutToEndId: sessionIdForGivenTask,
                reason: endSessionReason,
                triggeringTask: storeTask,
                currentPartner: storeTask.partner,
                agentSdk,
            });

            if (reason === "TechnicalIssues") {
                sdkEventBus.emit("technical_issues", true);
            }
            return state;
        });
    });
};

const onTaskCompleted = (task: Task, reason: TaskCompletedReason, agentSdk: AgentSdkBase) => {
    withSessionControlsCleanup((sessionControls) => {
        useSessionStore.getState().mutateStore((state) => {
            const storeTask = state.getTask(task.id);
            if (!storeTask) throw new Error(`Task ${task.id} not found in store`);

            const sessionIdForGivenTask = getSessionIdForTask(task, storeTask, state);
            if (!sessionIdForGivenTask) {
                throw new Error(`Task ${task.id} does not have a valid sessionId`);
            }

            updateTaskStatusInternal(state, task.id, "completed", reason);

            // Unbelievable‼️🤦‍♀️
            const endSessionReason: CustomerSessionEndedReason =
                reason === "TechnicalIssues"
                    ? "TechnicalIssues"
                    : isChatTask(storeTask)
                      ? "ChatEnded"
                      : "CallCompletedWithoutCallback";

            if (reason !== "CallbackInitiated") {
                sessionControls.addSessionEndArgs({
                    sessionAboutToEndId: sessionIdForGivenTask,
                    reason: endSessionReason,
                    triggeringTask: storeTask,
                    currentPartner: storeTask.partner,
                    agentSdk,
                });
            }

            if (reason === "TechnicalIssues") {
                sdkEventBus.emit("technical_issues", true);
            }
            return state;
        });
    });
};

/**
 * Deletes tasks that the current SDK can't handle (like Twilio tasks when only Connect SDK is active).
 * Prevents crashes when rehydrating old tasks after SDK switches.
 */
const removeUnsupportedTasksFromDifferentProvider = (
    state: Draft<SessionStore>,
    agentSdk: AgentSdkBase,
    activeTaskIds?: Set<string>,
) => {
    try {
        state.sessions.forEach((session) => {
            if (session.kind !== "with-customer") return;

            const tasksToRemove: string[] = [];

            session.tasks.forEach((task, taskId) => {
                // If task exists in backend, don't remove it
                if (activeTaskIds?.has(taskId)) {
                    return;
                }

                if (!agentSdk.canHandleTaskType(task)) {
                    logger.info(
                        { taskId, taskType: task.type, partner: task.partner, channel: env.channel },
                        "Unsupported task found, adding to deletion list",
                    );
                    tasksToRemove.push(taskId);
                }
            });

            if (tasksToRemove.length > 0) {
                tasksToRemove.forEach((taskId) => session.tasks.delete(taskId));
                logger.info(
                    { sessionId: session.id, purged: tasksToRemove.length, remaining: session.tasks.size },
                    "Deleted unsupported tasks",
                );
            }
        });
    } catch (error) {
        logger.warn({ error }, "Failed to delete unsupported tasks - continuing with potentially invalid tasks");
    }
};
/** Manually end an active session in cases where there will be no task actions to trigger from.
 *  @param sessionId - When provided, ends this specific session instead of relying on selectedSession().
 *                     This is important when called from endWrapup, where a new call may have changed the selected session.
 */
const completeSession = (agentSdk: AgentSdkBase, partner: Partner, sessionId?: string) => {
    tryCatch(() => {
        const currentSession = sessionId
            ? useSessionStore.getState().getSession(sessionId)
            : useSessionStore.getState().computed.selectedSession();

        logger.trace({ session: currentSession, sessionId }, "completing session");

        if (!currentSession) {
            logger.warn({ sessionId }, "Cannot complete session - session not found");
            return;
        }

        if (currentSession.currentTask) throw new Error("Cannot manually complete a session that has active task(s)");

        if (isChatTask(currentSession?.currentTask)) {
            triggerCxSessionEnd({
                sessionAboutToEndId: currentSession?.id,
                reason: "ChatEnded",
                triggeringTask: currentSession?.currentTask,
                currentPartner: partner,
                agentSdk,
            });
        } else {
            triggerCxSessionEnd({
                sessionAboutToEndId: currentSession?.id,
                reason: "CallCompletedWithoutCallback",
                triggeringTask: undefined,
                currentPartner: partner,
                agentSdk,
            });
        }
    });
};

const initStore = (
    sessionControls: SessionControls,
    state: Draft<SessionStore>,
    callbackSessionId: string | undefined,
    tasks: Task[] | undefined | null,
    agentSdk: AgentSdkBase,
) => {
    if (state.initialized) throw new Error("SessionStore already initialized");

    // To achieve the requirement that we do not expose tasks till post SDK/Twilio init
    // we hide tasks till SDK init and rehydrate here
    // rehydrated tasks are used in the case below to attribute incoming tasks to the correct session
    state.sessions.forEach((session) => {
        session.restoreHydratedTasks();
    });

    const agentHasTasks = !!tasks?.length;

    switch (env.channel) {
        case "voice": {
            const [partner] = agentSdk.getPartners();
            const currentSession = state.sessions[state.sessions.length - 1];

            // Session's active task is the same as agent active task, replace the non-class instance in the session with our instance Task
            if (agentHasTasks && currentSession?.currentTask?.id === tasks[0].id) {
                currentSession?.replaceTask(tasks[0]);
                state.initialized = true;

                logger.debug(
                    { currentSession },
                    "The session active task is the same as the agents active task during store init",
                );
                return state;
            }

            const handleAgentHasTasks = () => {
                // Satisfies the type checker
                if (!tasks) throw new Error("Unexpected undefined tasks");

                // We have tasks assigned, but current session has no task or has a different task association than the current task
                if (!currentSession || currentSession?.currentTask?.id !== tasks[0].id) {
                    // New task was created from an outbound call from this session, just add task to session and break
                    if (isOutboundVoiceTask(tasks[0]) && tasks[0].sessionId === currentSession?.id) {
                        currentSession?.addTask(tasks[0]);
                        return;
                    }
                    sessionControls.addSessionStartArgs({
                        currentSessionId: currentSession?.id,
                        reason: "CallAlreadyExists",
                        task: tasks[0],
                        agentSdk,
                    });
                    logger.debug(
                        { currentSession },
                        "The session active task is different from the agents active task during store init",
                    );
                }
            };

            const handleAgentDoesNotHaveTasks = () => {
                // We have existing wrapping state, defer to handlers of wrapping state to wrap or end session
                if (currentSession?.wrappingState) {
                    return;
                }

                if (callbackSessionId === currentSession?.id) {
                    logger.debug({ callbackSessionId }, "Callback session is active, not ending the session");
                    return;
                }

                //We only want to trigger session end events on active sessions that are with a customer
                if (currentSession?.kind === "with-customer" && currentSession?.metadata.status === "active") {
                    // Session is with a customer (on task), Agent has no tasks, and we are not waiting for a callback now, therefore we must end the session
                    // Regardless of if the session has or does not have an active task association
                    sessionControls.addSessionEndArgs({
                        sessionAboutToEndId: currentSession?.id,
                        reason: "CallUnexpectedlyEnded",
                        triggeringTask: undefined,
                        currentPartner: partner,
                        agentSdk,
                    });
                    logger.debug(
                        { currentSession, partner },
                        "The session has previously ended as the expert has no associated tasks during store init",
                    );
                }
            };

            if (agentHasTasks) {
                handleAgentHasTasks();
            } else {
                handleAgentDoesNotHaveTasks();
            }

            break;
        }
        case "chat": {
            logger.debug(
                { backendTasks: tasks?.length || 0, rehydratedSessions: state.sessions.length },
                "Chat init - rehydrating",
            );

            /** Set of task IDs used for quicker lookups  */
            const activeTaskIds = new Set(tasks?.map((task) => task.id) || []);

            /** Map to track which tasks are associated with sessions during processing */
            const associatedTasks = new Set<string>();

            // Associate active tasks with sessions and identify stale tasks
            state.sessions.forEach((session) => {
                if (session?.kind === "with-customer") {
                    const thisSessionsTaskIds = Array.from(session.tasks.keys());

                    // Pick partner from any first task with a partner property
                    let sessionPartner =
                        Array.from(session.tasks.values()).find((task) => task.partner)?.partner || "unknown";

                    thisSessionsTaskIds.forEach((taskId) => {
                        if (agentHasTasks && activeTaskIds.has(taskId)) {
                            const taskInstance = tasks?.find((t) => t.id === taskId);
                            if (taskInstance) {
                                session.replaceTask(taskInstance);
                                associatedTasks.add(taskId);

                                // Update partner from this valid task if available
                                sessionPartner = taskInstance.partner || sessionPartner;
                            }
                        }
                    });

                    const areAllTasksCompletedOrStale = Array.from(session.tasks.entries()).every(
                        ([taskId, task]) =>
                            ["completed", "cancelled"].includes(task.status) || !activeTaskIds.has(taskId),
                    );
                    // chat tasks don't have callback functionality, end the session if all tasks are completed or stale
                    if (areAllTasksCompletedOrStale) {
                        sessionControls.addSessionEndArgs({
                            sessionAboutToEndId: session.id,
                            reason: "ChatEnded",
                            triggeringTask: undefined,
                            currentPartner: sessionPartner,
                            agentSdk,
                        });
                    }
                }
            });

            // Skip cleanup if backend call failed (success with no tasks is [] and truthy)
            if (tasks) {
                removeUnsupportedTasksFromDifferentProvider(state, agentSdk, activeTaskIds);
            }

            // Start new sessions for tasks not associated with existing sessions
            if (agentHasTasks && tasks) {
                const unassociatedTasks = tasks.filter((task) => !associatedTasks.has(task.id));

                unassociatedTasks.forEach((taskInstance) => {
                    sessionControls.addSessionStartArgs({
                        currentSessionId: taskInstance.sessionId || taskInstance.sessionIdFallback,
                        reason: "InboundChat",
                        task: taskInstance,
                        agentSdk,
                    });
                });
            }

            break;
        }
    }

    state.initialized = true;
    return state;
};

const triggerCxSessionStart = ({ currentSessionId, reason, task }: SessionStartArgs) => {
    logger.trace({ reason, currentSessionId, task: (task as Task | undefined)?.toLog() }, "Starting Cx session");

    if (!isVoiceTask(task) && !isTrainingTask(task) && !isChatTask(task)) {
        throw new Error("Unsupported task type");
    }

    const newSessionId = task.sessionIdFallback;
    const existingSessionWithSameId = useSessionStore.getState().getSession(newSessionId);

    if (existingSessionWithSameId?.metadata.status === "ended") {
        logger.info(
            { oldSessionId: existingSessionWithSameId.id, newTaskId: task.id },
            "Removing ended session before creating new session with same ID",
        );
        useSessionStore.getState().clearInactiveSession(existingSessionWithSameId.id);
    }

    const newSession = new SessionInstance({
        /* sessionId is set in Twilio task attributes after the task (ExWo's one) is created.
           Due to the fact that we have auto-accept there is a race condition between updating task attributes and accepting it.
           We need to have sessionId available at earlier stages to avoid this race condition.
           So, we add task attribute sessionIdFallback in reservationCreated event handler before creating ExWo task.
           And then, we use it as a fallback in certain places when there is a chance that sessionId hasn't been set yet.
        */
        id: newSessionId,
        startedTimestamp: Date.now(),
        kind: task.hasCustomer ? "with-customer" : "without-customer",
        tasks: new Map([[task.id, task]]),
        wrappingState: null,
        metadata: {
            startReason: reason,
            status: "active",
        },
    });

    const shouldAutoSelect = !isChatTask(task); // If it's a chat task, we don't want to select the session immediately
    useSessionStore.getState().addSession(newSession, shouldAutoSelect);

    void onSessionStart(newSession as Session, task.partner);

    sdkEventBus.emit("session_changed", {
        action: "start",
        session: newSession as Session,
        reason,
    });
};

const triggerCxSessionEnd = ({
    sessionAboutToEndId,
    reason,
    triggeringTask,
    currentPartner,
    agentSdk,
}: SessionEndArgs) => {
    logger.debug(
        { reason, currentSessionId: sessionAboutToEndId, triggeringTask: triggeringTask?.toLog() },
        "Ending Cx session",
    );

    useSessionStore.getState().endSession(sessionAboutToEndId, reason);

    const sessionAboutToEnd = useSessionStore.getState().getSession(sessionAboutToEndId);

    if (!sessionAboutToEnd) throw new Error(`Session ${sessionAboutToEndId} not found`);

    if (
        reason === "CallCancelled" ||
        reason === "CallCompletedWithoutCallback" ||
        reason === "CallRejected" ||
        reason === "TechnicalIssues"
    ) {
        void handleEndSessionSetAgentActivity(reason, agentSdk);
    } else if (reason === "ChatEnded") {
        const sessions = useSessionStore.getState().sessions;
        const areAllTasksCompleted = sessions.every(({ tasks }) =>
            Array.from(tasks.values()).every(({ status }) => ["completed", "cancelled"].includes(status)),
        );

        if (areAllTasksCompleted) {
            void handleEndSessionSetAgentActivity(reason, agentSdk);
        }
    }

    const sessionAboutToEndPartner = currentPartner;

    const wrapupEndedCallback = ({
        session,
        type,
        message,
    }: {
        session: Session;
        type: "success" | "error";
        message: string;
    }) => {
        sdkEventBus.emit("wrapup_ended", {
            session,
            type,
            message,
        });
    };

    onSessionEnd(sessionAboutToEnd, sessionAboutToEndPartner, wrapupEndedCallback);

    sdkEventBus.emit("session_changed", {
        action: "end",
        session: sessionAboutToEnd,
        reason,
    });
};

const handleEndSessionSetAgentActivity = async (reason: CustomerSessionEndedReason, agentSdk: AgentSdkBase) => {
    const { pendingActivity } = useAgentStore.getState();
    logger.debug({ pendingActivity, reason }, "==== TROUBLESHOOT: session ended pending activity and reason ====");

    // NOTE: We should never get any string other than the ones defined in the AgentActivity type, but it looks
    //       like somehow we're sometimes getting the string '"undefined"' (the quotes included). The following
    //       if check is there to confirm whether this is happening and report it when it does happen.

    // eslint-disable-next-line prefer-smart-quotes/prefer
    if (pendingActivity === '"undefined"') {
        logger.warn(
            "==== TROUBLESHOOT: pending activity wrong status ====: Somehow pendingActivity was the string undefined",
        );
    }

    const [partner] = agentSdk.getPartners();
    const { technicalIssues } = getAppConfigSingleton().getPartnersConfig(partner);

    const activeTasks = useSessionStore.getState().computed.selectedTasks();
    if (technicalIssues?.autoSet && reason === "TechnicalIssues") {
        await agentSdk.setAgentActivity(technicalIssues.agentActivity);
    } else if (
        agentSdk.getAgentActivity() === "Callback Pending" &&
        !hasPendingCallback() &&
        agentSdk.serviceProvider === "connect"
    ) {
        await agentSdk.setAgentActivity(agentSdk.availableAgentActivity);
    } else if (pendingActivity && (agentSdk.defaultAgentActivity !== "Offline" || env.channel === "chat")) {
        await agentSdk.setAgentActivity(pendingActivity);
    } else if (!agentSdk.hasActivitySelfManagement && !activeTasks.length) {
        // If we have an active task, we do NOT want to go available, such as during a callback
        await agentSdk.setAgentActivity(agentSdk.availableAgentActivity);
    }
};

const getSessionIdForTask = (
    task: Task,
    sessionStoreTask: Task,
    state: WritableDraft<SessionStore>,
): string | undefined => {
    // If the task has a sessionId, use it. Otherwise, use the sessionId from the storeTask or the fallback.
    // If none of these are available, try to get the sessionId from the state using the task id.
    // If no sessionId is found, log an error.
    const sessionId =
        task.sessionId ?? sessionStoreTask.sessionId ?? task.sessionIdFallback ?? state.getSessionByTaskId(task.id)?.id;
    if (!sessionId) {
        logger.warn({ taskId: task.id }, "Task does not have a valid sessionId");
    }

    return sessionId;
};

const onTaskError = (task: Task | undefined, agentSdk: AgentSdkBase, error?: unknown) => {
    const errorLogger = logger.child({ action: "onTaskError", taskId: task?.id, error });
    errorLogger.info("Handling task error");

    const callbackState = useCallbackStore.getState().callbackState;
    if (callbackState) {
        const originalTask = callbackState.originalTask;
        errorLogger.info({ originalTaskId: originalTask.id }, "Using original task from callback state");

        clearCallbackState();
        void agentSdk.startWrapup("TaskWrapping", originalTask).catch((startWrapupError) =>
            errorLogger.error(
                { startWrapupError, originalTaskId: originalTask.id },
                // TODO(callback-failures): If startWrapup fails, callback state is already cleared and the UI can lose both callback bar and summary.
                // Add a fallback strategy (restore callback state or force wrapping marker) so the agent is never left in limbo.
                "startWrapup failed for callback",
            ),
        );
        return;
    }

    const isOutboundCallFailure =
        task &&
        isVoiceTask(task) &&
        task.callDirection === "outbound" &&
        (task.outboundKind === "CallbackNow" || !!task.previousTaskId);

    if (isOutboundCallFailure) {
        errorLogger.info({ taskId: task.id }, "Outbound callback task error - triggering wrapping");
        if (task.status !== "completed" && task.status !== "wrapping") {
            useSessionStore.getState().updateTaskStatus(task.id, "wrapping");
        }
        void agentSdk.startWrapup("TaskWrapping", task).catch((startWrapupError) =>
            errorLogger.error(
                { startWrapupError, taskId: task.id },
                // TODO(callback-failures): Mirror fallback handling here (restore callback state or set wrapping fallback) if wrapup start fails.
                "startWrapup failed for callback task",
            ),
        );
        return;
    }

    errorLogger.warn("No callback state or callback task - cannot trigger session summary for this error");
};

export const sessionsOrchestrator = {
    initSessions,
    addNewAgentTask,
    onTaskRejected,
    onTaskCancelled,
    onTaskCompleted,
    onTaskError,
    completeSession,
};
